import React from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  TextField,
  Typography,
  Link,
  IconButton,
  OutlinedInput,
  InputLabel,
  InputAdornment,
  FormControl,
  FormHelperText,
  Alert,
  Autocomplete,
  Grid,
  Paper,
} from "@mui/material";

const BRAND = "#f05d32";

const MyCouponsBtn = styled(Link)(() => ({
  color: BRAND,
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
  borderBottom: `1px solid ${BRAND}`,
  pointerEvents: "auto",
  height: 22,
  "&:hover": {
    borderBottom: "1px solid transparent",
  },
}));

const PromoCode = styled(Alert)(() => ({
  marginTop: "8px",
  border: `1px solid ${BRAND}`,
  backgroundColor: BRAND,
  position: "relative",
  textAlign: "center",
  alignItems: "center",
  borderRadius: "50px",
  color: "#fff",
  fontSize: 15,
  fontWeight: 600,
  "& .MuiAlert-action": {
    padding: 0,
  },
  "& .MuiAlert-message": {
    width: "100%",
  },
}));

const CouponAutocomplete = styled(Autocomplete)(() => ({
  marginTop: "8px",
  backgroundColor: "#fff",
  borderRadius: "12px",
  "& label.Mui-focused": {
    color: BRAND,
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.12)",
      borderRadius: "12px",
    },
    "&:hover fieldset": {
      borderColor: "rgba(240, 93, 50, 0.45)",
    },
    "&.Mui-focused fieldset": {
      borderColor: BRAND,
      borderWidth: 1.5,
    },
  },
}));

const CouponOptions = styled(Box)(() => ({
  color: "#1a1a1a",
  padding: "10px 16px",
  fontWeight: 500,
  minHeight: "auto !important",
  width: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  cursor: "pointer",
  "&:hover, &:focus": {
    color: BRAND,
    backgroundColor: "rgba(240, 93, 50, 0.06)",
  },
  "& > i": {
    marginRight: "8px",
    color: BRAND,
  },
}));

const NewCouponField = styled(FormControl)(() => ({
  margin: "8px 0px",
  width: "100%",
  "& label.Mui-focused": {
    color: BRAND,
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.12)",
      borderRadius: "12px",
    },
    "&:hover fieldset": {
      borderColor: "rgba(240, 93, 50, 0.45)",
    },
    "&.Mui-focused fieldset": {
      borderColor: BRAND,
      borderWidth: 1.5,
    },
  },
}));

export default function CouponsSection(props) {
  const { component } = props;
  const devHelper = component.getHelpers("dev");

  const { order = null, guest = false } = component.getData("default", {});

  const coupons = component.getData("default.coupons", []);

  const [couponAutocomplete, showCouponAutocomplete] = React.useState(true);

  return (
    <>
      {order && (
        <Paper
          elevation={0}
          sx={{
            px: { xs: 2, md: 2.5 },
            py: { xs: 2, md: 2.5 },
            borderRadius: "16px",
            backgroundColor: "#fff",
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 18, sm: 20, md: 22 },
              lineHeight: 1.2,
              mb: 2,
              color: "#1a1a1a",
            }}
          >
            {component.ucfirst("checkout-title-2")}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={8} md={7}>
              {!devHelper.getObjectValue(order, "coupon") ? (
                <>
                  {!guest && coupons.length > 1 && couponAutocomplete ? (
                    <>
                      <Typography
                        sx={{ fontSize: 14, color: "#6b6b65", mb: 0.5 }}
                      >
                        {component.ucfirst("checkout-subtitle-2-2")}
                      </Typography>
                      <CouponAutocomplete
                        id="mui-autocomplete"
                        fullWidth={true}
                        autoComplete={false}
                        options={coupons}
                        autoHighlight
                        getOptionLabel={(option) => option.name}
                        renderOption={(props, option) => {
                          return option.slug !== "add" ? (
                            <CouponOptions
                              key={option._id}
                              onClick={() =>
                                component.activateCoupon(option._id)
                              }
                            >
                              <i className="fa-light fa-ticket" />
                              {option.name}
                            </CouponOptions>
                          ) : (
                            <CouponOptions
                              id={option.slug}
                              key={option.slug}
                              onClick={() => showCouponAutocomplete(false)}
                              sx={{ textAlign: "center" }}
                            >
                              <i className="fa-light fa-plus"></i>
                              {option.name}
                            </CouponOptions>
                          );
                        }}
                        renderInput={(params) => {
                          return (
                            <TextField
                              {...params}
                              label={component.trans("promo-code")}
                              variant="outlined"
                              inputProps={{
                                ...params.inputProps,
                                autoComplete: "new-password",
                              }}
                            />
                          );
                        }}
                      />
                    </>
                  ) : (
                    <Box>
                      <Typography
                        sx={{ fontSize: 14, color: "#6b6b65", mb: 0.5 }}
                      >
                        {component.ucfirst("checkout-subtitle-2-1")}
                      </Typography>
                      <form onSubmit={(e) => component.updateCouponByCode(e)}>
                        <NewCouponField
                          variant="outlined"
                          fullWidth
                          name="couponCode"
                          type="text"
                          id="couponCode"
                          error={component.dataExists("error.couponCode")}
                        >
                          <InputLabel>
                            {component.trans("promo-code")}
                          </InputLabel>
                          <OutlinedInput
                            placeholder={component.trans("promo-holder")}
                            value={component.getData("default.couponCode", "")}
                            onChange={(e) => {
                              component.setData({
                                "default.couponCode": e.target.value,
                              });
                            }}
                            label={component.trans("promo-code")}
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  disableRipple
                                  type="submit"
                                  sx={{
                                    color: BRAND,
                                    fontSize: "28px",
                                    "&:hover, &:focus": {
                                      backgroundColor: "transparent",
                                    },
                                  }}
                                >
                                  <i className="fa-solid fa-circle-chevron-right" />
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                          {component.dataExists("error.couponCode") && (
                            <FormHelperText
                              error
                              style={{ margin: "3px 14px 0" }}
                            >
                              {component.getData("error.couponCode")}
                            </FormHelperText>
                          )}
                        </NewCouponField>
                      </form>

                      {!guest && coupons.length > 1 && (
                        <Box sx={{ mt: 1 }}>
                          <MyCouponsBtn
                            underline="none"
                            onClick={() => showCouponAutocomplete(true)}
                          >
                            {component.ucfirst("my-coupons")}
                          </MyCouponsBtn>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <PromoCode
                  onClose={() => {
                    component.releaseCoupon();
                    showCouponAutocomplete(true);
                  }}
                  elevation={0}
                  variant="filled"
                  icon={false}
                >
                  {devHelper.getObjectValue(order, "coupon.name")}
                </PromoCode>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}
    </>
  );
}
